export interface RequestQueue {
    add<T>(request: () => Promise<T>): Promise<T>;
    getRunningCount(): number;
    getQueueLength(): number;
}

export interface RequestQueueTelemetry {
    requestQueueEnabled: boolean;
    requestEnqueued: boolean;
    queueLengthAtEnqueue: number;
    runningCountAtEnqueue: number;
    queueWaitTimeMs: number;
    queueMaxConcurrency?: number;
    mailboxApiConcurrencyAtStart?: number;
}

interface ExecuteRequestWithTelemetryOptions<T> {
    requestQueue?: RequestQueue;
    queueMaxConcurrency?: number;
    concurrencyKey: string;
    trackConcurrency?: boolean;
    request: () => Promise<T>;
    onRequestStart: (telemetry: RequestQueueTelemetry) => void;
}

const activeRequestCounts = new Map<string, number>();

export function executeRequestWithTelemetry<T>({ requestQueue, queueMaxConcurrency, concurrencyKey, trackConcurrency = true, request, onRequestStart, }: ExecuteRequestWithTelemetryOptions<T>): Promise<T> {
    const requestQueueEnabled = requestQueue !== undefined;
    const queueLengthAtEnqueue = requestQueue?.getQueueLength() ?? 0;
    const runningCountAtEnqueue = requestQueue?.getRunningCount() ?? 0;
    const requestEnqueued = requestQueueEnabled &&
        queueMaxConcurrency !== undefined &&
        runningCountAtEnqueue >= queueMaxConcurrency;
    const enqueuedAt = performance.now();
    
    const executeRequest = async (): Promise<T> => {
        const mailboxApiConcurrencyAtStart = trackConcurrency
            ? (activeRequestCounts.get(concurrencyKey) ?? 0) + 1
            : undefined;
        if (mailboxApiConcurrencyAtStart !== undefined) {
            activeRequestCounts.set(concurrencyKey, mailboxApiConcurrencyAtStart);
        }
        
        try {
            onRequestStart({
                requestQueueEnabled,
                requestEnqueued,
                queueLengthAtEnqueue,
                runningCountAtEnqueue,
                queueWaitTimeMs: performance.now() - enqueuedAt,
                queueMaxConcurrency,
                mailboxApiConcurrencyAtStart,
            });
            return await request();
        }
        finally {
            if (trackConcurrency) {
                const remainingRequests = (activeRequestCounts.get(concurrencyKey) ?? 1) - 1;
                if (remainingRequests === 0) {
                    activeRequestCounts.delete(concurrencyKey);
                }
                else {
                    activeRequestCounts.set(concurrencyKey, remainingRequests);
                }
            }
        }
    };
    
    return requestQueue ? requestQueue.add(executeRequest) : executeRequest();
}

export function createRequestQueue(maxRequests: number): RequestQueue {
    let requestCounter = 0;
    const queue: (() => void)[] = [];
    
    function add<T>(request: () => Promise<T>): Promise<T> {
        return new Promise((resolve, reject) => {
            const runRequest = (): void => {
                requestCounter = requestCounter + 1;
                request().then((value: T): void => {
                    requestCompleted();
                    resolve(value);
                }, error => {
                    requestCompleted();
                    reject(error);
                });
            };
            if (requestCounter === maxRequests) {
                queue.push(runRequest);
            }
            else {
                runRequest();
            }
        });
    }
    
    function requestCompleted(): void {
        requestCounter = requestCounter - 1;
        if (queue.length > 0) {
            queue.shift()?.();
        }
    }
    
    return {
        add,
        getRunningCount: () => requestCounter,
        getQueueLength: () => queue.length,
    };
}
